// FulfillmentEntryPanel -- Slice 1D.
//
// Single-piece entry form (top) + Bulk import button that opens the
// FulfillmentEntryBulkImportModal (mirrors ManufacturingBulkImportModal
// pattern, including the max-h-[90vh] overflow-y-auto outer container).
//
// The single-piece form picks origin (cash_purchase or repo_intake),
// optional plant_id, optional intake_template_id, and optional notes,
// then POSTs to /api/fulfillment/entry/single. On success it shows the
// issued sentinel serial (CASH-#### or REPO-####) and clears the form.

import { useEffect, useState } from "react";
import {
  createFulfillmentEntrySingle,
  listFulfillmentIntakeTemplates,
  type FulfillmentIntakeTemplate,
} from "@/lib/fulfillment/fulfillmentApi";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { FULFILLMENT_ENTRY_ORIGINS, type MfgUnitOrigin } from "@/lib/fulfillment/originEnum";
import { FulfillmentEntryBulkImportModal } from "./FulfillmentEntryBulkImportModal";

export function FulfillmentEntryPanel() {
  const [origin, setOrigin] = useState<MfgUnitOrigin>("cash_purchase");
  const [plantId, setPlantId] = useState("");
  const [notes, setNotes] = useState("");
  const [templateId, setTemplateId] = useState<string>("");
  const [templates, setTemplates] = useState<FulfillmentIntakeTemplate[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ serial: string; intakeFormId: string | null } | null>(
    null
  );
  const [showBulk, setShowBulk] = useState(false);

  useEffect(() => {
    void (async () => {
      const r = await listFulfillmentIntakeTemplates();
      if (r.ok) setTemplates(r.data.templates);
    })();
  }, []);

  function clearForm() {
    setNotes("");
    setPlantId("");
    setTemplateId("");
  }

  async function submit() {
    setBusy(true);
    setError(null);
    setSuccess(null);
    const r = await createFulfillmentEntrySingle({
      origin,
      plant_id: plantId.trim() || undefined,
      notes: notes.trim() || undefined,
      intake_template_id: templateId || null,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setSuccess({ serial: r.data.serial, intakeFormId: r.data.intake_form_id });
    clearForm();
  }

  const filteredTemplates = templates.filter(
    (t) => t.is_active && (t.applies_to_origins.length === 0 || t.applies_to_origins.includes(origin))
  );

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">
          Fulfillment — Inventory entry
        </h2>
        <button
          type="button"
          onClick={() => setShowBulk(true)}
          className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium dark:border-zinc-700 dark:bg-zinc-900"
        >
          Bulk import…
        </button>
      </div>

      <div
        {...{ [FIREFLY_TARGET_ATTR]: "ff-entry-form" }}
        className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900"
      >
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Single piece</h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Origin
            </span>
            <select
              value={origin}
              onChange={(e) => setOrigin(e.target.value as MfgUnitOrigin)}
              className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
            >
              {FULFILLMENT_ENTRY_ORIGINS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Plant (optional)
            </span>
            <input
              type="text"
              value={plantId}
              onChange={(e) => setPlantId(e.target.value)}
              placeholder="leave blank if none"
              className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
            />
          </label>

          <label className="block sm:col-span-2">
            <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Intake template
            </span>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
            >
              <option value="">(none — no intake form will be created)</option>
              {filteredTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label} ({t.slug})
                </option>
              ))}
            </select>
            {filteredTemplates.length === 0 ? (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                No active templates for origin <code>{origin}</code>. Admin can author one in the
                Intake forms mode.
              </p>
            ) : null}
          </label>

          <label className="block sm:col-span-2">
            <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Notes
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
            />
          </label>
        </div>

        {error ? (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {error}
          </div>
        ) : null}
        {success ? (
          <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 dark:border-green-800 dark:bg-green-950/35 dark:text-green-100">
            Created unit with serial <code>{success.serial}</code>
            {success.intakeFormId
              ? ` · intake form ${success.intakeFormId.slice(0, 8)}`
              : " · no intake form (no template applied)"}
            .
          </div>
        ) : null}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            {...{ [FIREFLY_TARGET_ATTR]: "ff-entry-create" }}
            onClick={() => void submit()}
            disabled={busy}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? "Creating…" : "Create unit"}
          </button>
        </div>
      </div>

      {showBulk ? (
        <FulfillmentEntryBulkImportModal
          templates={templates}
          onClose={() => setShowBulk(false)}
          onImported={() => {
            // Bulk import leaves the modal open so the user can paste more
            // rows; refreshing the template list is a no-op here.
          }}
        />
      ) : null}
    </div>
  );
}
